import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import axios from "axios";
import { APP_LINK_PATTERN, parseAppLink, toAppWebUrl } from "@/shared/utils/appLinks";
import { fetchFolderContents, getBreadcrumb } from "@/features/files/services/sharepointService";
import { announcementService } from "@/features/announcements/services/feedService";

/**
 * Resolves our own links in a piece of text (chat message, announcement body) to
 * display labels — "📄 <file>", "📁 <folder>", "📢 <announcement title>" — so the
 * name can be shown instead of the raw URL. Mirrors hp-chat-v1's hook.
 *
 * There are no single-item endpoints, so:
 * - file names come from the folder the link carries (folder contents),
 * - folder names from the folder's breadcrumb,
 * - announcement titles from the user's announcement list, which the backend
 *   already filters to what this user may see.
 * Lookups are cached for 5 minutes and shared by every message on screen.
 */

const TRAILING_PUNCTUATION = /[.,!?;:'"]+$/;
const CACHE_TTL_MS = 5 * 60 * 1000;

type CacheEntry<T> = { at: number; promise: Promise<T> };

// folderId → (fileId → display name); null = folder not available to this user
const fileNameCache = new Map<string, CacheEntry<Map<string, string> | null>>();
// folderId → folder name; null = folder not available to this user
const folderNameCache = new Map<string, CacheEntry<string | null>>();
// single entry: row_id → title (raw value; may be a per-language object)
const announcementCache = new Map<string, CacheEntry<Map<number, unknown> | null>>();

const isUnavailable = (err: unknown) =>
  axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 404);

/**
 * Returns the cached lookup for `key`, loading it if missing or stale. A 403/404
 * is cached as null (show "unavailable"); any other failure (offline, 5xx) is
 * not cached and rethrown, so the caller keeps the plain URL and retries later.
 */
const cachedLookup = <T>(
  cache: Map<string, CacheEntry<T | null>>,
  key: string,
  load: () => Promise<T>,
): Promise<T | null> => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  const promise = load().catch((err) => {
    if (isUnavailable(err)) return null;
    cache.delete(key);
    throw err;
  });
  cache.set(key, { at: Date.now(), promise });
  return promise;
};

/** Announcement titles can be a plain string or a { en, ja, ... } object. */
const localizedText = (value: unknown, language: string): string | null => {
  if (typeof value === "string") return value || null;
  if (value && typeof value === "object") {
    const byLang = value as Record<string, unknown>;
    const text = byLang[language] ?? byLang.en ?? Object.values(byLang)[0];
    return typeof text === "string" && text ? text : null;
  }
  return null;
};

/** Unique app links in `text`, with sentence punctuation trimmed off the end. */
export const findAppLinks = (text?: string | null): string[] => {
  if (!text) return [];
  const matches = (text.match(APP_LINK_PATTERN) ?? [])
    .map((m) => m.replace(TRAILING_PUNCTUATION, ""))
    .filter((m) => parseAppLink(m) !== null);
  return [...new Set(matches)];
};

/** Label for `href` (as a linkifier found it), tolerating trailing punctuation. */
export const appLinkLabelFor = (labels: Record<string, string>, href: string): string | undefined =>
  labels[href] ?? labels[href.replace(TRAILING_PUNCTUATION, "")];

const resolveLabel = async (url: string, t: TFunction, language: string): Promise<string | null> => {
  const link = parseAppLink(url);
  if (!link) return null;

  if (link.type === "announcement") {
    const titles = await cachedLookup(announcementCache, "all", async () => {
      const list = await announcementService.getAnnouncements();
      const rows: Array<{ row_id?: number | string; id?: number | string; title?: unknown }> =
        Array.isArray(list) ? list : [];
      return new Map(rows.map((a) => [Number(a.row_id ?? a.id), a.title]));
    });
    const title = localizedText(titles?.get(link.rowId), language);
    return `📢 ${title ?? t("feed.unavailable_announcement")}`;
  }

  if (link.fileId) {
    const fileId = link.fileId;
    const names = await cachedLookup(fileNameCache, link.folderId, async () => {
      const { files } = await fetchFolderContents(link.folderId);
      return new Map((files ?? []).map((f) => [String(f.id), f.display_name]));
    });
    return `📄 ${names?.get(fileId) ?? t("sharepoint.unavailable_file")}`;
  }

  const name = await cachedLookup(folderNameCache, link.folderId, async () => {
    const crumbs = await getBreadcrumb(link.folderId);
    // [root, ...ancestors, folder]; a lone root means the backend sent no trail.
    return crumbs.length > 1 ? crumbs[crumbs.length - 1].name : null;
  });
  return `📁 ${name ?? t("sharepoint.unavailable_folder")}`;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Characters that would otherwise turn a name like "plan_v2_final" into
// italics, or close the link text early.
const escapeMarkdown = (value: string) => value.replace(/[\\`*_~|[\]]/g, "\\$&");

/**
 * Rewrites each labelled URL in `text` as a markdown link `[label](url)`, for
 * renderers that understand markdown (Stream chat). URLs that are already the
 * target or text of a markdown link are left alone. Native-scheme links point
 * at the equivalent web URL, since a browser can't open hp-chat-v1://.
 */
export const withAppLinkLabels = (text: string, labels: Record<string, string>): string => {
  let result = text;
  for (const [url, label] of Object.entries(labels)) {
    result = result.replace(new RegExp(escapeRegExp(url), "g"), (match, offset: number, whole: string) => {
      const before = whole.slice(Math.max(0, offset - 2), offset);
      const after = whole.charAt(offset + match.length);
      if (before === "](" || before.endsWith("[") || after === "]") return match;
      return `[${escapeMarkdown(label)}](${toAppWebUrl(url)})`;
    });
  }
  return result;
};

const NO_LABELS: Record<string, string> = {};

/** Map of app URL (as it appears in `text`) → display label, filled in as lookups finish. */
export const useAppLinkLabels = (text?: string | null): Record<string, string> => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.split("-")[0] || "en";
  // Labels are kept with the text they were resolved for, so a stale result
  // never shows on a message whose text has since changed.
  const [resolved, setResolved] = useState<{ text: string; labels: Record<string, string> }>({
    text: "",
    labels: {},
  });

  useEffect(() => {
    const urls = findAppLinks(text);
    if (!text || urls.length === 0) return;

    let alive = true;
    Promise.all(
      urls.map((url) =>
        resolveLabel(url, t, language)
          .then((label) => [url, label] as const)
          .catch(() => [url, null] as const),
      ),
    ).then((entries) => {
      if (!alive) return;
      const next: Record<string, string> = {};
      for (const [url, label] of entries) {
        if (label) next[url] = label;
      }
      setResolved({ text, labels: next });
    });

    return () => {
      alive = false;
    };
  }, [text, t, language]);

  return text && resolved.text === text ? resolved.labels : NO_LABELS;
};
